/** Shared helpers for fetching and opening document files.
 *
 * The download endpoint returns either JSON `{ url }` (an Azure SAS URL in
 * production) or the raw file bytes (local dev). Both paths are handled here
 * so pages and the preview modal share one implementation.
 */

export const PREVIEWABLE_TYPES = new Set(["pdf", "jpg", "jpeg", "png", "gif"]);

/** Fetch a document's bytes regardless of SAS-URL vs streamed response. */
export async function fetchDocumentBytes(docId: number): Promise<ArrayBuffer> {
  const resp = await fetch(`/api/documents/${docId}/download?disposition=inline`, {
    credentials: "include",
  });
  if (!resp.ok) throw new Error("Failed to fetch document");
  const ct = resp.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) {
    const data = (await resp.json()) as { url: string };
    const fileResp = await fetch(data.url);
    if (!fileResp.ok) throw new Error("Failed to fetch document");
    return fileResp.arrayBuffer();
  }
  return resp.arrayBuffer();
}

/** Legacy open-in-new-tab / download behaviour, kept for the Download button
 *  and as the "Open in new tab" escape hatch inside the preview modal. */
export async function openDocument(
  docId: number,
  disposition: "inline" | "attachment",
): Promise<void> {
  try {
    const resp = await fetch(
      `/api/documents/${docId}/download?disposition=${disposition}`,
      { credentials: "include" },
    );
    if (!resp.ok) throw new Error("Download failed");
    const ct = resp.headers.get("content-type") ?? "";
    if (ct.includes("application/json")) {
      const data = (await resp.json()) as { url: string };
      window.open(data.url, "_blank");
    } else {
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      if (disposition === "inline") {
        window.open(url, "_blank");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = "";
        a.click();
        URL.revokeObjectURL(url);
      }
    }
  } catch {
    /* ignore */
  }
}
